import { Link } from "@tanstack/react-router";

import { AuthorAvatar } from "./author-avatar";
import type { BlogAuthor } from "../types";
import { cn } from "@/lib/utils";

/**
 * Who signed a post: their faces overlapping and their names. `detailed` adds the first author's
 * role under the names, for the top of an article; cards keep it to one line.
 */
export const Byline = ({
  authors,
  detailed,
  linked,
  className,
}: {
  authors: BlogAuthor[];
  detailed?: boolean;
  /** Names lead to the authors' pages. Off inside a card, which is itself one link. */
  linked?: boolean;
  className?: string;
}) => {
  if (authors.length === 0) return null;
  const size = detailed ? "size-10" : "size-6";
  return (
    <div className={cn("flex min-w-0 items-center gap-3", className)}>
      <div className="flex shrink-0 -space-x-2">
        {authors.map((author) => (
          <AuthorAvatar key={author.id} author={author} className={cn(size, "ring-2 ring-background")} />
        ))}
      </div>
      <div className="min-w-0">
        <p className={cn("truncate font-medium", detailed ? "text-sm" : "text-[13px]")}>
          {authors.map((author, index) => (
            <span key={author.id}>
              {index > 0 ? <span className="text-muted-foreground">{index === authors.length - 1 ? " & " : ", "}</span> : null}
              {linked ? (
                <Link
                  to="/blog/author/$authorSlug"
                  params={{ authorSlug: author.slug }}
                  className="transition-colors hover:text-[var(--site-ink)]"
                >
                  {author.name}
                </Link>
              ) : (
                author.name
              )}
            </span>
          ))}
        </p>
        {detailed && authors[0]?.role ? (
          <p className="truncate text-[13px] text-muted-foreground">
            {authors.length === 1 ? authors[0].role : authors.map((author) => author.role).filter(Boolean).join(" · ")}
          </p>
        ) : null}
      </div>
    </div>
  );
};
